/**
 * Any Campus Access (Biostar) API calls with React Query.
 * Use CacheKeys QUERY_KEYS.campusAccess.root to invalidate.
 */
import { GetCampusAccessStatusCancelToken, GetStudentCampusBuildingsCancelToken } from "../../services/StudentsSettingServices";
import { appQueryClient } from "../appQueryClient";
import { QUERY_CACHE_PROFILES, QUERY_KEYS } from "../queryConsts";
import { useBaseReactQuery } from "./useBaseReactQuery";

const EMPTY_CAMPUS_BUILDINGS = Object.freeze([]);

/**
 * Invalidates all campus-access-related queries (status + campus buildings).
 * Both active queries will automatically refetch eventually.
 */
export function invalidateCampusAccessQueries() {
    return appQueryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.campusAccess.root]
    });
}

/**
 * Campus Access - A student's Biostar campus access status.
 * @param {number|string} studentId sp_students.id
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useCampusAccessStatus(studentId) {
    return useBaseReactQuery(
        [QUERY_KEYS.campusAccess.root, QUERY_KEYS.campusAccess.accessStatus, studentId],
        async () => {
            const { data } = await GetCampusAccessStatusCancelToken(studentId);
            return data;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: !!studentId
        }
    );
}

/**
 * Campus Access - A student's assigned campus building(s).
 * @param {number|string} studentId sp_students.id
 * @returns {DefinedUseQueryResult} React Query result
 */
export function useStudentCampusBuildings(studentId) {
    const query = useBaseReactQuery(
        [QUERY_KEYS.campusAccess.root, QUERY_KEYS.campusAccess.campusBuildings, studentId],
        async () => {
            const { data } = await GetStudentCampusBuildingsCancelToken(studentId);
            return data?.buildings ?? EMPTY_CAMPUS_BUILDINGS;
        },
        {
            cacheProfile: QUERY_CACHE_PROFILES.DEFAULT,
            enabled: !!studentId
        }
    );
    return { ...query, data: query.data ?? EMPTY_CAMPUS_BUILDINGS };
}
